import type { AssetCatalog } from './assets';
import { SFX_CATEGORY, SFX_PREVIEW, SOUND_CATEGORIES, type SoundCategory } from './sounds';

/** Parte do `HTMLAudioElement` que o player usa; permite trocar por um falso nos testes. */
export interface AudioElementLike {
  src: string;
  loop: boolean;
  volume: number;
  muted: boolean;
  onended?: (() => void) | null;
  /** Navegadores antigos (e o jsdom) podem devolver undefined em vez de uma promessa. */
  play(): Promise<void> | undefined;
  pause(): void;
}

export type AudioFactory = () => AudioElementLike;

/** Caminho lógico (no manifest) de uma trilha (`audio/1001.mp3`) e de um efeito (`audio/sfx/008.mp3`). */
export const musicPath = (name: string | number): string => `audio/${name}.mp3`;
export const sfxPath = (id: string): string => `audio/sfx/${id}.mp3`;

/** Volume e mudo de uma categoria do mixer. */
export interface ChannelState {
  /** 0 a 1. */
  volume: number;
  muted: boolean;
}

/** Estado completo do mixer: o volume geral (mestre) e o de cada categoria. O volume que se ouve é `geral × categoria`. */
export interface MixerState {
  master: ChannelState;
  channels: Record<SoundCategory, ChannelState>;
}

export const DEFAULT_MIXER: Readonly<MixerState> = {
  master: { volume: 1, muted: false },
  channels: {
    music: { volume: 0.4, muted: false },
    combat: { volume: 0.6, muted: false },
    ui: { volume: 0.6, muted: false },
    movement: { volume: 0.6, muted: false },
    turn: { volume: 0.6, muted: false },
  },
};

const clamp01 = (value: number): number => (Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0);

export function defaultMixer(): MixerState {
  return {
    master: { ...DEFAULT_MIXER.master },
    channels: Object.fromEntries(SOUND_CATEGORIES.map((c) => [c.id, { ...DEFAULT_MIXER.channels[c.id] }])) as Record<SoundCategory, ChannelState>,
  };
}

/** Aceita um estado guardado de uma versão antiga ou corrompida: o que faltar ou vier inválido volta ao padrão. */
export function parseMixer(raw: unknown): MixerState {
  const mixer = defaultMixer();
  if (typeof raw !== 'object' || raw === null) return mixer;
  const read = (value: unknown, into: ChannelState) => {
    if (typeof value !== 'object' || value === null) return;
    const { volume, muted } = value as Partial<ChannelState>;
    if (typeof volume === 'number' && Number.isFinite(volume)) into.volume = clamp01(volume);
    if (typeof muted === 'boolean') into.muted = muted;
  };
  const source = raw as { master?: unknown; channels?: Record<string, unknown> };
  read(source.master, mixer.master);
  for (const { id } of SOUND_CATEGORIES) read(source.channels?.[id], mixer.channels[id]);
  return mixer;
}

export interface AudioPlayerOptions {
  assets?: AssetCatalog | null;
  /** Volume inicial da música (categoria Música). */
  musicVolume?: number;
  /** Volume inicial dos efeitos (todas as categorias de efeito). */
  sfxVolume?: number;
  /** Estado inicial do mixer (o que o jogador guardou); tem prioridade sobre `musicVolume` e `sfxVolume`. */
  mixer?: MixerState;
  muted?: boolean;
  createAudio?: AudioFactory;
}

export interface SfxOptions {
  loop?: boolean;
  /** Padrão true: um novo disparo reinicia o mesmo som. Com false, se ele já está tocando, não faz nada. */
  replace?: boolean;
  /** Categoria do mixer; por padrão a do efeito em `SFX_CATEGORY`. */
  category?: SoundCategory;
}

interface RunningEffect {
  el: AudioElementLike;
  category: SoundCategory;
}

/**
 * Toca a música (uma trilha por vez) e os efeitos (vários ao mesmo tempo), passando tudo pelo mixer: cada som pertence a uma categoria
 * com volume e mudo próprios, e o volume geral vale para todas. Os navegadores bloqueiam som antes de um gesto do usuário:
 * quando o `play()` da música é recusado, ela fica pendente e `resume()` (chamado no próximo clique/tecla) tenta de novo.
 * Pode ser criado antes do manifest: a música pedida espera por `setAssets`.
 */
export class AudioPlayer {
  private readonly create: AudioFactory;
  private assets: AssetCatalog | null;
  private music: AudioElementLike | null = null;
  private current: string | null = null;
  private wanted: { candidates: Array<string | number>; loop: boolean } | null = null;
  private pending = false;
  private readonly effects = new Map<string, RunningEffect>();
  private readonly listeners = new Set<(mixer: MixerState) => void>();
  private _muted: boolean;
  private mixer: MixerState;

  constructor(options: AudioPlayerOptions = {}) {
    this.assets = options.assets ?? null;
    this.create = options.createAudio ?? (() => new Audio() as unknown as AudioElementLike);
    this._muted = options.muted ?? false;
    this.mixer = options.mixer ? parseMixer(options.mixer) : defaultMixer();
    if (!options.mixer) {
      if (options.musicVolume !== undefined) this.mixer.channels.music.volume = clamp01(options.musicVolume);
      if (options.sfxVolume !== undefined) {
        for (const { id } of SOUND_CATEGORIES) if (id !== 'music') this.mixer.channels[id].volume = clamp01(options.sfxVolume);
      }
    }
  }

  get muted(): boolean {
    return this._muted;
  }

  /** Caminho da trilha tocando agora. */
  get playing(): string | null {
    return this.current;
  }

  /** Cópia do estado do mixer (para a interface mostrar as barras). */
  getMixer(): MixerState {
    return parseMixer(this.mixer);
  }

  /** Volume que se ouve de uma categoria agora: geral × categoria, ou 0 se estiver muda (ela, o geral ou o mudo total). */
  effectiveVolume(category: SoundCategory): number {
    const channel = this.mixer.channels[category];
    if (this._muted || this.mixer.master.muted || channel.muted) return 0;
    return clamp01(this.mixer.master.volume * channel.volume);
  }

  /** Avisa a cada mudança do mixer (a interface guarda a preferência do jogador). Devolve a função que cancela. */
  subscribe(listener: (mixer: MixerState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  setMasterVolume(volume: number): void {
    this.mixer.master.volume = clamp01(volume);
    this.changed();
  }

  setMasterMuted(muted: boolean): void {
    this.mixer.master.muted = muted;
    this.changed();
  }

  setChannelVolume(category: SoundCategory, volume: number): void {
    this.mixer.channels[category].volume = clamp01(volume);
    this.changed();
  }

  setChannelMuted(category: SoundCategory, muted: boolean): void {
    this.mixer.channels[category].muted = muted;
    this.changed();
  }

  /** Troca o estado inteiro (ex.: restaurar o padrão). */
  setMixer(mixer: MixerState): void {
    this.mixer = parseMixer(mixer);
    this.changed();
  }

  /** Toca o som de exemplo de uma categoria de efeitos, para o jogador ouvir o volume escolhido. */
  preview(category: SoundCategory): void {
    const id = SFX_PREVIEW[category];
    if (id) this.sfx(id, { category });
  }

  setAssets(assets: AssetCatalog | null): void {
    this.assets = assets;
    if (assets && this.wanted && !this.current) this.playFirst(this.wanted.candidates, this.wanted.loop);
  }

  /** Toca a primeira trilha que existir no manifest. Sem manifest ainda, guarda o pedido. Devolve o caminho tocado ou null. */
  playFirst(candidates: Array<string | number>, loop = true): string | null {
    this.wanted = { candidates, loop };
    const assets = this.assets;
    if (!assets) return null;
    for (const candidate of candidates) {
      const path = musicPath(candidate);
      if (assets.has(path)) {
        this.play(path, loop);
        return path;
      }
    }
    this.stopMusic();
    return null;
  }

  play(path: string, loop = true): void {
    const assets = this.assets;
    if (!assets || !assets.has(path)) return;
    if (this.current === path && this.music) return;
    this.stopMusic();
    const el = this.create();
    el.src = assets.url(path);
    el.loop = loop;
    el.volume = this.effectiveVolume('music');
    el.muted = this._muted;
    this.music = el;
    this.current = path;
    this.start(el);
  }

  /** Para a música e esquece o pedido pendente. */
  stop(): void {
    this.wanted = null;
    this.stopMusic();
  }

  /** Toca um efeito (`audio/sfx/<id>.mp3`). Falhas (sem manifest, sem som, navegador bloqueando) são silenciosas. */
  sfx(id: string, options: SfxOptions = {}): void {
    const path = sfxPath(id);
    const assets = this.assets;
    const category = options.category ?? SFX_CATEGORY[id] ?? 'combat';
    if (!assets || !assets.has(path) || this._muted) return;
    // Categoria muda ou no zero: nem cria o elemento (um laço de efeito, como a carga da força, recomeça ao desmutar).
    const volume = this.effectiveVolume(category);
    if (volume <= 0) return;
    const running = this.effects.get(id);
    if (running) {
      if (options.replace === false) return;
      running.el.pause();
    }
    const el = this.create();
    el.src = assets.url(path);
    el.loop = options.loop ?? false;
    el.volume = volume;
    el.muted = this._muted;
    el.onended = () => {
      if (this.effects.get(id)?.el === el) this.effects.delete(id);
    };
    this.effects.set(id, { el, category });
    el.play()?.catch(() => {
      if (this.effects.get(id)?.el === el) this.effects.delete(id);
    });
  }

  stopSfx(id: string): void {
    this.effects.get(id)?.el.pause();
    this.effects.delete(id);
  }

  setMuted(muted: boolean): void {
    this._muted = muted;
    if (this.music) this.music.muted = muted;
    if (muted) {
      for (const { el } of this.effects.values()) el.pause();
      this.effects.clear();
    }
  }

  /** Volume da música (categoria Música); mantido por compatibilidade com quem só tem um controle. */
  setVolume(volume: number): void {
    this.setChannelVolume('music', volume);
  }

  /** Tenta de novo uma trilha que o navegador recusou por falta de gesto do usuário. */
  resume(): void {
    if (this.pending && this.music) this.start(this.music);
  }

  /** Aplica o mixer ao que já está tocando e avisa quem guarda a preferência. */
  private changed(): void {
    if (this.music) this.music.volume = this.effectiveVolume('music');
    for (const [id, { el, category }] of this.effects) {
      const volume = this.effectiveVolume(category);
      if (volume <= 0) {
        // Categoria silenciada no meio do som: corta (o laço de carga recomeça quando o jogador carregar de novo).
        el.pause();
        this.effects.delete(id);
      } else {
        el.volume = volume;
      }
    }
    const snapshot = this.getMixer();
    for (const listener of this.listeners) listener(snapshot);
  }

  private stopMusic(): void {
    this.music?.pause();
    this.music = null;
    this.current = null;
    this.pending = false;
  }

  private start(el: AudioElementLike): void {
    this.pending = false;
    el.play()?.catch(() => {
      if (this.music === el) this.pending = true;
    });
  }
}
